import type { ReactNode } from 'react'
import type { MessagingConversation } from '../../../api/generated/model/messagingConversation'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import { formatRelative } from '../../../lib/datetime'

type ConversationListVariant = 'default' | 'compact' | 'expanded'

type ConversationListProps = {
  conversations: MessagingConversation[]
  selectedId: string | null
  onSelect: (conversationId: string) => void
  isLoading?: boolean
  counterpartLabels?: Record<string, string>
  supportAction?: ReactNode
  variant?: ConversationListVariant
}

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  isLoading = false,
  counterpartLabels = {},
  supportAction,
  variant = 'default',
}: ConversationListProps) {
  const t = useT(messagingI18n)
  const isMessenger = variant !== 'default'

  const containerClass = isMessenger
    ? 'min-h-full bg-[#11151c] text-white'
    : undefined
  const emptyClass = isMessenger
    ? 'p-5 text-sm text-slate-400'
    : 'p-5 text-sm text-slate-500'

  if (isLoading) {
    return (
      <div
        className={
          isMessenger
            ? 'p-5 text-sm text-slate-400'
            : 'p-5 text-sm text-slate-500'
        }
        data-variant={isMessenger ? variant : undefined}
        role="status"
        aria-live="polite"
      >
        {t.states.loading}
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div
        className={containerClass}
        data-variant={isMessenger ? variant : undefined}
      >
        {supportAction}
        <div className={emptyClass}>
          {t.conversation.noConversations}
        </div>
      </div>
    )
  }

  return (
    <div
      className={containerClass}
      data-variant={isMessenger ? variant : undefined}
    >
      {supportAction}
      <ul
        className={
          isMessenger
            ? 'divide-y divide-white/10'
            : 'divide-y divide-slate-100'
        }
        aria-label={t.conversation.listLabel}
      >
      {conversations.map((conversation) => {
        const selected = conversation.id === selectedId
        const isSupport = conversation.entity_link.entity_type === 'organization'
        const title = isSupport
          ? t.conversation.linkedSupport
          : conversation.subject || conversation.entity_link.label
        const unreadLabel =
          conversation.unread_count > 0
            ? t.launcher.unread(conversation.unread_count)
            : null
        const buttonClass = isMessenger
          ? selected
            ? 'border-l-[var(--color-action)] bg-white/10'
            : 'border-l-transparent bg-transparent hover:bg-white/[0.06]'
          : selected
            ? 'border-l-[var(--color-action)] bg-[var(--color-selected)]'
            : 'border-l-transparent bg-[var(--color-surface)] hover:bg-[var(--color-surface-muted)]'
        return (
          <li key={conversation.id}>
            <button
              type="button"
              aria-current={selected ? 'true' : undefined}
              aria-label={[title, conversation.entity_link.label, unreadLabel]
                .filter(Boolean)
                .join(', ')}
              onClick={() => onSelect(conversation.id)}
              className={`w-full border-l-2 px-4 text-left transition-colors focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] ${isMessenger ? 'py-3.5' : 'py-3'} ${buttonClass}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p
                    className={
                      isMessenger
                        ? 'truncate text-sm font-semibold text-white'
                        : 'truncate text-sm font-semibold text-[var(--color-text)]'
                    }
                  >
                    {title}
                  </p>
                  {counterpartLabels[conversation.id] ? (
                    <p className="mt-0.5 truncate text-xs font-semibold text-[var(--color-action)]">
                      {counterpartLabels[conversation.id]}
                    </p>
                  ) : null}
                  {conversation.entity_link.label !== title ? (
                    <p
                      className={
                        isMessenger
                          ? 'mt-0.5 truncate text-xs font-medium text-slate-400'
                          : 'mt-0.5 truncate text-xs font-medium text-[var(--color-text-muted)]'
                      }
                    >
                      {conversation.entity_link.label}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <time
                    dateTime={conversation.updated_at}
                    className={
                      isMessenger
                        ? 'text-[11px] font-medium text-slate-400'
                        : 'text-[11px] font-medium text-[var(--color-text-muted)]'
                    }
                  >
                    {formatRelative(conversation.updated_at)}
                  </time>
                  {conversation.unread_count > 0 ? (
                    <span
                      aria-hidden="true"
                      className="inline-flex min-w-5 justify-center rounded-full bg-[var(--color-signal)] px-1.5 py-0.5 text-[11px] font-bold text-white"
                    >
                      {conversation.unread_count}
                    </span>
                  ) : null}
                </div>
              </div>
              {conversation.last_message_preview ? (
                <p
                  className={
                    isMessenger
                      ? 'mt-2 line-clamp-2 text-xs leading-4 text-slate-400'
                      : 'mt-2 line-clamp-2 text-xs leading-4 text-[var(--color-text-muted)]'
                  }
                >
                  {conversation.last_message_preview}
                </p>
              ) : null}
            </button>
          </li>
        )
      })}
      </ul>
    </div>
  )
}
